import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import MainLayout from '../components/MainLayout';
import {
  BookOpen, ChevronDown, ChevronRight, Check, X, ArrowUpRight,
  Building2, Users, Coins, ShieldCheck, Calculator, Calendar, Banknote,
  FileBarChart, AlertCircle, Loader2, Printer, Layers,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useActiveCompany } from '../hooks/useActiveCompany';

const API = process.env.REACT_APP_BACKEND_URL + '/api/admin/setup-progress';

const SECTIONS = [
  { id: 'setup', icon: Building2, color: 'blue', title: '1. One-Time Setup' },
  { id: 'employees', icon: Users, color: 'cyan', title: '2. Employee Onboarding' },
  { id: 'ctc', icon: Layers, color: 'violet', title: '3. CTC Group Setup (Multi-Scenario)' },
  { id: 'pay-cycle', icon: Calendar, color: 'amber', title: '4. Pay Cycle & Pay Days Management' },
  { id: 'attendance', icon: Calculator, color: 'teal', title: '5. Attendance Linkage' },
  { id: 'run', icon: Banknote, color: 'emerald', title: '6. Payroll Run (the main event)' },
  { id: 'statutory', icon: ShieldCheck, color: 'indigo', title: '7. Statutory Filings' },
  { id: 'reports', icon: FileBarChart, color: 'rose', title: '8. Reports & Reconciliation' },
  { id: 'troubleshoot', icon: AlertCircle, color: 'orange', title: '9. Troubleshooting / FAQ' },
];

export default function ProcessingGuide() {
  const [activeCompany] = useActiveCompany();
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(new Set(['setup']));

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const qs = activeCompany ? `?company_id=${activeCompany}` : '';
        const { data } = await axios.get(`${API}${qs}`, { withCredentials: true });
        setProgress(data.sections);
      } catch (e) {
        setProgress(null);
      } finally { setLoading(false); }
    })();
  }, [activeCompany]);

  const toggle = (id) => {
    const next = new Set(open);
    next.has(id) ? next.delete(id) : next.add(id);
    setOpen(next);
  };

  return (
    <MainLayout>
      <div className="space-y-5" data-testid="processing-guide-page">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
              <BookOpen className="inline-block w-8 h-8 mr-2 mb-1 text-[#0052FF]" />
              Processing Guide
            </h1>
            <p className="mt-1 text-sm text-slate-600">
              End-to-end playbook for setting up and running payroll. Each step is clickable — jump straight to the screen.
            </p>
          </div>
          <Button variant="outline" onClick={() => window.print()} data-testid="guide-print-btn">
            <Printer className="w-4 h-4 mr-1" /> Print / Save as PDF
          </Button>
        </div>

        {!activeCompany && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-800 text-sm">
            Pick a company in the top header to see live progress indicators.
          </div>
        )}

        {/* Master progress bar */}
        {loading ? (
          <div className="bg-white border border-slate-200 rounded-lg p-4 text-sm text-slate-500">
            <Loader2 className="w-4 h-4 inline mr-2 animate-spin" /> Computing progress…
          </div>
        ) : progress ? <OverallProgress progress={progress} /> : null}

        {/* Sections */}
        <div className="space-y-3">
          {SECTIONS.map((s) => {
            const Icon = s.icon;
            const isOpen = open.has(s.id);
            return (
              <div key={s.id} className="bg-white border border-slate-200 rounded-lg overflow-hidden"
                data-testid={`guide-section-${s.id}`}>
                <button onClick={() => toggle(s.id)}
                  data-testid={`guide-toggle-${s.id}`}
                  className={`w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors`}>
                  <div className="flex items-center gap-3">
                    <span className={`flex items-center justify-center w-8 h-8 rounded-lg bg-${s.color}-100 text-${s.color}-700`}>
                      <Icon className="w-4 h-4" />
                    </span>
                    <h2 className="text-sm font-semibold text-slate-900 text-left">{s.title}</h2>
                  </div>
                  {isOpen ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                </button>
                {isOpen && <div className="px-4 pb-4 border-t border-slate-100">
                  {s.id === 'setup' && <SectionSetup p={progress} />}
                  {s.id === 'employees' && <SectionEmployees p={progress} />}
                  {s.id === 'ctc' && <SectionCTC p={progress} />}
                  {s.id === 'pay-cycle' && <SectionPayCycle p={progress} />}
                  {s.id === 'attendance' && <SectionAttendance p={progress} />}
                  {s.id === 'run' && <SectionRun p={progress} />}
                  {s.id === 'statutory' && <SectionStatutory p={progress} />}
                  {s.id === 'reports' && <SectionReports p={progress} />}
                  {s.id === 'troubleshoot' && <SectionTroubleshoot />}
                </div>}
              </div>
            );
          })}
        </div>
      </div>
    </MainLayout>
  );
}

// =============== Helpers ===============

const StatusPill = ({ ok, label, count }) => (
  <span data-testid={`pill-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
      ok ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-700'
    }`}>
    {ok ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
    {label}: {count != null ? count : ok ? 'done' : 'pending'}
  </span>
);

const Step = ({ num, title, children, link }) => (
  <div className="pt-3 border-t border-slate-100">
    <div className="flex items-start justify-between gap-3">
      <div className="flex-1">
        <h4 className="text-sm font-semibold text-slate-800">
          <span className="inline-block w-6 text-slate-400 font-mono">{num}.</span>
          {title}
        </h4>
        <div className="mt-1 ml-6 text-xs text-slate-600 space-y-1">{children}</div>
      </div>
      {link && (
        <Link to={link} className="shrink-0 mt-1">
          <Button variant="outline" size="sm" data-testid={`go-${link.replace(/[^a-z0-9]/gi, '-')}`}>
            Go to screen <ArrowUpRight className="w-3 h-3 ml-1" />
          </Button>
        </Link>
      )}
    </div>
  </div>
);

const Note = ({ children }) => (
  <div className="bg-blue-50 border-l-4 border-blue-300 px-3 py-2 text-xs text-blue-800 rounded-r mt-2">
    💡 {children}
  </div>
);

const Example = ({ title, children }) => (
  <div className="bg-slate-50 border border-slate-200 rounded p-3 mt-2 text-xs">
    <p className="font-semibold text-slate-700 mb-1">📋 {title}</p>
    <div className="text-slate-600">{children}</div>
  </div>
);

const Code = ({ children }) => <code className="px-1 py-0.5 bg-slate-100 rounded font-mono text-[11px] text-rose-700">{children}</code>;

// =============== Overall progress strip ===============

function OverallProgress({ progress }) {
  const buckets = [
    { name: 'Setup', done: progress.one_time_setup.salary_heads > 0 },
    { name: 'Statutory', done: (progress.statutory_masters.pf_configs + progress.statutory_masters.esi_configs) > 0 },
    { name: 'Wage Master', done: progress.wage_master.skill_categories > 0 },
    { name: 'CTC Groups', done: progress.ctc_groups.total > 0 },
    { name: 'Employees', done: progress.employees.total > 0 },
    { name: 'Pay Cycle', done: progress.pay_cycle.monthly_pay_days_overrides > 0 || progress.ctc_groups.total > 0 },
    { name: 'Attendance', done: progress.attendance.attendance_records > 0 || progress.attendance.leave_policies > 0 },
    { name: 'Payroll Run', done: progress.payroll_run.runs_total > 0 },
  ];
  const completed = buckets.filter((b) => b.done).length;
  const pct = Math.round((completed / buckets.length) * 100);
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="guide-overall-progress">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-slate-700">Setup Completion</h3>
        <span className="text-2xl font-bold text-[#0052FF] font-mono">{pct}%</span>
      </div>
      <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className="h-full bg-gradient-to-r from-[#0052FF] to-emerald-500 transition-all"
          style={{ width: `${pct}%` }} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3">
        {buckets.map((b) => (
          <div key={b.name} className={`flex items-center gap-2 px-2 py-1 rounded text-xs ${
            b.done ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-50 text-slate-500'
          }`}>
            {b.done ? <Check className="w-3 h-3" /> : <div className="w-3 h-3 rounded-full border border-current" />}
            {b.name}
          </div>
        ))}
      </div>
    </div>
  );
}

// =============== 1. Setup ===============

function SectionSetup({ p }) {
  const s = p?.one_time_setup || {};
  const st = p?.statutory_masters || {};
  return (
    <div>
      <p className="text-xs text-slate-600 mt-2">
        Build the foundational data the entire system depends on. Do this <b>once</b> per company.
      </p>
      <div className="flex flex-wrap gap-1 mt-2">
        <StatusPill ok={s.companies > 0} label="Companies" count={s.companies} />
        <StatusPill ok={s.users > 1} label="Users" count={s.users} />
        <StatusPill ok={s.departments > 0} label="Departments" count={s.departments} />
        <StatusPill ok={s.locations > 0} label="Locations" count={s.locations} />
        <StatusPill ok={s.designations > 0} label="Designations" count={s.designations} />
        <StatusPill ok={s.salary_heads >= 5} label="Salary Heads" count={s.salary_heads} />
      </div>

      <Step num="1.1" title="Add Companies + Branches" link="/companies">
        Tenant Admin → Companies. Each legal entity (Pvt Ltd / LLP / Branch) is a separate Company.
        <Note>You can switch between companies via the dropdown in the top header — every screen scopes data to the active company.</Note>
      </Step>
      <Step num="1.2" title="Set up Roles + Users" link="/users">
        Create roles (HR Admin / Payroll Admin / Finance Reviewer / Department Manager / Employee).
        Assign permissions per role from <Code>/roles</Code>.
        Add users with their email & role; they'll receive an invite mail.
      </Step>
      <Step num="1.3" title="Master Setup — Departments / Locations / Designations" link="/masters/setup">
        Without these, employees can't be assigned org structure. Use the bulk CSV uploader on each tab to add 100s of rows quickly.
      </Step>
      <Step num="1.4" title="Salary Heads Master" link="/masters/setup">
        Master Setup → Payroll tab → <b>Salary Heads</b> card → <b>+ Add</b>.
        Define every component you'll use:
        BASIC, HRA, EXGRATIA, BONUS, EL_ENCASHMENT (earnings) ·
        PF, ESI, PT, TDS (deductions) ·
        PF_ER, ESI_ER (employer contributions).
        Each row has <b>9 flags</b> that drive the payroll engine:
        <ul className="list-disc ml-4 mt-1 space-y-0.5">
          <li><b>Head Type</b> — Earning / Deduction / Employer Contribution / Reimbursement</li>
          <li><b>Attendance-linked</b> — prorate by payable days (Basic / HRA usually ON; Bonus usually OFF)</li>
          <li><b>Part of CTC / Part of Gross / Affects Net</b> — what the head adds to</li>
          <li><b>Taxable / Show on Payslip</b> — TDS + payslip visibility</li>
          <li><b>PF / ESI / PT Applicable</b> — counts toward statutory wage base</li>
        </ul>
        <Note>Edits flow into the payroll engine on the next run (no manual sync needed).</Note>
      </Step>
      <Step num="1.5" title="Statutory Masters — PF / ESI / PT / LWF / TDS" link="/statutory/setup">
        <ul className="list-disc ml-4 space-y-0.5">
          <li><b>PF</b>: 12% employee + 12% employer split (8.33% pension + 3.67% PF), wage cap ₹15,000.</li>
          <li><b>ESI</b>: 0.75% employee + 3.25% employer, applicable up to ₹21,000 gross.</li>
          <li><b>PT</b>: state-wise slabs (e.g. TN: nil up to ₹3,500, ₹16.66 then ₹208).</li>
          <li><b>TDS</b>: New regime / Old regime slabs, surcharge, cess.</li>
        </ul>
        <div className="flex flex-wrap gap-1 mt-2">
          <StatusPill ok={st.pf_configs > 0} label="PF" count={st.pf_configs} />
          <StatusPill ok={st.esi_configs > 0} label="ESI" count={st.esi_configs} />
          <StatusPill ok={st.pt_configs > 0} label="PT" count={st.pt_configs} />
          <StatusPill ok={st.lwf_configs > 0} label="LWF" count={st.lwf_configs} />
          <StatusPill ok={st.tds_slabs > 0} label="TDS Slabs" count={st.tds_slabs} />
        </div>
      </Step>
    </div>
  );
}

// =============== 2. Employees ===============

function SectionEmployees({ p }) {
  const e = p?.employees || {};
  return (
    <div>
      <p className="text-xs text-slate-600 mt-2">
        Onboard employees with all the metadata that drives payroll: company, department, designation, joining date, salary, and (for O&M) skill category + daily rate.
      </p>
      <div className="flex flex-wrap gap-1 mt-2">
        <StatusPill ok={e.total > 0} label="Total" count={e.total} />
        <StatusPill ok={e.active > 0} label="Active" count={e.active} />
        <StatusPill ok={e.with_ctc_assigned > 0} label="With CTC" count={e.with_ctc_assigned} />
        <StatusPill ok={e.with_skill_category > 0} label="With Skill" count={e.with_skill_category} />
      </div>

      <Step num="2.1" title="Add Employees (Single or Bulk)" link="/employees">
        Click <b>+ Add Employee</b> for one-by-one entry, OR <b>📥 Bulk Upload</b> for CSV import (template button on the same row).
        <Note>The bulk-upload template auto-prefixes employee codes per your tenant's setting (e.g. <Code>HECS001</Code>, <Code>HECS002</Code>).</Note>
      </Step>
      <Step num="2.2" title="Set CTC / Compensation" link="/payroll/ctc-planner">
        For Staff (CTC-based) employees: assign annual CTC + a Salary Structure Template — the engine auto-splits BASIC / HRA / etc.
        Or use Bulk Upload to import 100s of CTCs from a single CSV.
      </Step>
      <Step num="2.3" title="Set Skill Category + Daily Rate (O&M / Labour only)" link="/employees">
        Open any employee → in the edit modal scroll to the amber-bordered <b>O&M Per-Day-Wage Fields</b> section. Set:
        <ul className="list-disc ml-4 mt-1 space-y-0.5">
          <li><b>Skill Category</b> — Highly Skilled / Skilled / Semi Skilled / Unskilled / Apprentice / your own</li>
          <li><b>Daily Rate Override</b> — leave blank to use the Wage Master rate, or fill to override</li>
          <li><b>Location</b> — refines wage-rate resolution (Chennai vs Bangalore)</li>
        </ul>
      </Step>
      <Step num="2.4" title="Bulk-Assign Employees to CTC Groups" link="/business-controls">
        Business Controls → CTC Groups. The 109-group bulk-mapper accepts a CSV with employee_code → group_code lookups.
      </Step>
    </div>
  );
}

// =============== 3. CTC Groups (multi-scenario) ===============

function SectionCTC({ p }) {
  const c = p?.ctc_groups || {};
  return (
    <div>
      <p className="text-xs text-slate-600 mt-2">
        CTC Groups are <b>the heart of payroll</b>. Each group defines: salary heads + formulas, base days, OT, pay cycle, and most importantly the <b>calc_method</b> — Staff (CTC-based) vs O&M (per-day-wage).
      </p>
      <div className="flex flex-wrap gap-1 mt-2">
        <StatusPill ok={c.total > 0} label="Groups" count={c.total} />
        <StatusPill ok={c.ctc_based > 0} label="Staff (CTC)" count={c.ctc_based} />
        <StatusPill ok={c.per_day_wage >= 0} label="O&M (Wage)" count={c.per_day_wage} />
      </div>

      <Step num="3.1" title="Scenario A — Staff (CTC-based) Group" link="/business-controls">
        <p>Most white-collar employees. Engine derives Basic / HRA / Special / etc. from monthly CTC.</p>
        <Example title="Setup steps">
          <ol className="list-decimal ml-4 space-y-1">
            <li>+ Add New Group · <b>Calc Method = CTC Based</b></li>
            <li>Parent Group: <Code>Staff</Code>, Sub-Group: blank or <Code>Senior</Code></li>
            <li>Salary Heads:
              <ul className="list-disc ml-4 mt-1">
                <li>BASIC = 50% of <Code>CTC</Code> (mark <b>ctc_component</b> ✔)</li>
                <li>HRA = 50% of <Code>BASIC</Code> ✔</li>
                <li>SPECIAL_ALLOWANCE = <b>Balancing</b> (auto-fills the gap)</li>
                <li>PF (deduction) = 12% of <Code>BASIC</Code> capped at ₹1,800/month</li>
                <li>PF_ER (employer) = 12% of <Code>BASIC</Code></li>
              </ul>
            </li>
            <li>Base Days = 30 (monthly), Pay Cycle = 1st-30th</li>
            <li>Save → assign employees via Employee Master</li>
          </ol>
        </Example>
        <Example title="Worked example: ₹6,00,000 annual CTC">
          Monthly CTC = ₹50,000 → BASIC = ₹25,000 · HRA = ₹12,500 · PF = ₹1,800 (capped) ·
          Special Allowance = balance to fit gross = ₹12,500. Net Pay ≈ ₹48,200.
        </Example>
      </Step>

      <Step num="3.2" title="Scenario B — O&M / Labour (Per-Day Wage) Group" link="/business-controls">
        <p>Blue-collar / contract workers paid by the day. The engine resolves a daily rate from Wage Master (skill × act × location), then computes Basic = rate × payable_days.</p>
        <Example title="Setup steps">
          <ol className="list-decimal ml-4 space-y-1">
            <li>Wage Master → Wage Rates → add row: <Code>Highly Skilled / TN Industrial Act / Chennai → ₹1,008/day</Code></li>
            <li>Business Controls → CTC Groups → + Add. <b>Calc Method = Per-Day Wage</b>; Wage Basis Act = "TN Industrial Establishment Act"; Default Location = "Chennai"</li>
            <li>Salary Heads:
              <ul className="list-disc ml-4 mt-1">
                <li>BASIC = fixed (engine overrides with rate × days)</li>
                <li>HRA = 50% of BASIC</li>
                <li>EXGRATIA = 4.81% of BASIC</li>
                <li>STATUTORY_BONUS = 8.33% of BASIC</li>
                <li>EL_ENCASHMENT = 5% of BASIC</li>
              </ul>
            </li>
            <li>Base Days = 26 (typical for blue-collar), Pay Cycle = 26th-25th</li>
            <li>Set employee's Skill Category to <Code>Highly Skilled</Code> on Employee Master</li>
          </ol>
        </Example>
        <Example title="Worked example: 26 payable days">
          ₹1,008 × 26 = <b>BASIC ₹26,208</b> · HRA ₹13,104 · Bonus ₹2,183 · Exgratia ₹1,261 · EL ₹1,310 · Gross ≈ <b>₹44,066</b>
        </Example>
        <Note>The daily rate falls back gracefully: Skill+Act+Location → Skill+Act → fail-loud preflight blocker.</Note>
      </Step>

      <Step num="3.3" title="Scenario C — Mixed Staff + O&M with different cycles" link="/business-controls">
        Most enterprises run BOTH simultaneously. Use parent groups to model it:
        <Example title="Org structure">
          <pre className="font-mono text-[10px] leading-relaxed">
{`Parent: Staff           (calc_method: ctc_based,    cycle 1-30)
  └─ STAFF_EXEC          (BASIC=50% CTC, HRA=50% BASIC)
  └─ STAFF_ADMIN
Parent: O&M              (calc_method: per_day_wage, cycle 26-25)
  └─ Sub: O&M IOCL
       └─ OM_IOCL_MECH   (Skilled, ₹1,008/day)
       └─ OM_IOCL_ELEC   (Highly Skilled, ₹1,150/day)
  └─ Sub: O&M Plumbing
       └─ OM_PLUMB_T1    (Semi Skilled, ₹742/day)`}
          </pre>
        </Example>
        <Note>The <b>Group Tree Picker</b> in Payroll Run lets you process Staff and O&M in separate batches within the same monthly run.</Note>
      </Step>

      <Step num="3.4" title="Bulk Hierarchy + Cycle Mapper" link="/business-controls">
        Got 100+ groups already? Click <b>🌳 Export Parent / Sub / Cycle</b> → edit the CSV → re-upload via <b>📤 Bulk Map Hierarchy + Cycle</b>. One file remaps everything.
      </Step>
    </div>
  );
}

// =============== 4. Pay Cycle ===============

function SectionPayCycle({ p }) {
  const pc = p?.pay_cycle || {};
  return (
    <div>
      <p className="text-xs text-slate-600 mt-2">
        Pay cycle = the <b>attendance window</b> a payroll period covers. Different employee types can have different cycles in the same month.
      </p>

      {/* ✅ Important clarification — there's now ONE calendar source of truth. */}
      <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mt-3">
        <p className="text-xs font-semibold text-blue-900 mb-1">📅 Where do calendars live?</p>
        <p className="text-[11px] text-blue-800">
          There is now <b>ONE</b> payroll calendar list shared across both screens — same data, two views:
        </p>
        <ul className="list-disc ml-4 mt-1 text-[11px] text-blue-800 space-y-0.5">
          <li>
            <b>Business Controls → Calendar Config</b>: rich editor with split-period boundaries (21-to-20, 26-to-25), statutory-month rules, and the <b>"Generate 12 periods"</b> button.
          </li>
          <li>
            <b>Payroll Setup → Calendars / Periods</b>: simplified view with quick edits + period-by-period management.
          </li>
        </ul>
        <p className="text-[11px] text-blue-800 mt-1">
          A calendar created in either place shows up in both. Older duplicated rows are auto-migrated the first time you open the Business Controls calendar list.
        </p>
      </div>

      <div className="flex flex-wrap gap-1 mt-2">
        <StatusPill ok={pc.monthly_pay_days_overrides > 0} label="Pay-Days Overrides" count={pc.monthly_pay_days_overrides} />
      </div>

      <Step num="4.1" title="What is a Pay Cycle?">
        <ul className="list-disc ml-4 space-y-0.5">
          <li><b>1-30 / 1-31</b>: Standard monthly cycle (Staff). April pay = April 1 → April 30 attendance.</li>
          <li><b>26-25</b>: Cross-month cycle (O&M / Blue collar). April pay = March 26 → April 25 attendance.</li>
          <li><b>16-15</b>: Mid-month cycle (less common). April pay = March 16 → April 15.</li>
        </ul>
      </Step>

      <Step num="4.2" title="Set Pay Cycle on the CTC Group" link="/business-controls">
        Inside the CTC Group editor → <b>Pay Cycle</b> section → set <Code>pay_cycle_start_day</Code> and <Code>pay_cycle_end_day</Code>.
        Quick presets: 1-30, 21-20, 26-25.
        <Note>The engine automatically anchors the cycle on each period's payout month (handles month-length differences + year-end crossings).</Note>
      </Step>

      <Step num="4.3" title="Monthly Pay Days Override" link="/payroll/wage-master">
        Pay cycle defines the <i>window</i>; pay-days defines <i>how many days of salary</i> for that period.
        Most months are 30 (or 26 for O&M), but special months may differ:
        <Example title="Example overrides">
          <ul className="list-disc ml-4">
            <li>Apr-2026 / Parent <Code>Staff</Code> / 30 days (default)</li>
            <li>Apr-2026 / Parent <Code>O&M</Code> / 26 days</li>
            <li>Feb-2026 / Parent <Code>Staff</Code> / 28 days (short month)</li>
          </ul>
        </Example>
        Wage Master → Monthly Pay Days → Add Override. Choose scope: <b>Parent Group</b> (covers all subs) OR <b>Specific CTC Group</b>.
      </Step>

      <Step num="4.4" title="Resolution precedence">
        When the engine looks up pay-days for a group in a given period, it picks the <b>most specific</b>:
        <ol className="list-decimal ml-4 mt-1 space-y-0.5">
          <li>Period × specific CTC group → highest priority</li>
          <li>Period × parent group → next</li>
          <li>CTC group's <Code>base_days_fixed</Code> → fallback</li>
          <li>Default 30 → last resort</li>
        </ol>
      </Step>
    </div>
  );
}

// =============== 5. Attendance ===============

function SectionAttendance({ p }) {
  const a = p?.attendance || {};
  return (
    <div>
      <p className="text-xs text-slate-600 mt-2">
        Attendance is the source of truth for <b>payable_days, LOP, leaves, and OT</b>. It MUST be closed and approved before a payroll run is processed.
        Day-to-day attendance currently flows <b>manually after verification</b> (auto-sync from device is opt-in).
      </p>

      {/* Day-to-day flow at a glance */}
      <div className="bg-teal-50 border border-teal-200 rounded-md p-3 mt-3 text-[11px]">
        <p className="font-semibold text-teal-900 mb-1">📌 The daily attendance flow (read this first)</p>
        <ol className="list-decimal ml-4 mt-1 text-teal-900 space-y-0.5">
          <li><b>Capture</b> — Face / fingerprint device records punches. Gateway agent forwards to cloud.</li>
          <li><b>Ingest</b> — Punches land in <code className="bg-white px-1 rounded">device_punch_logs</code> as raw rows (unprocessed).</li>
          <li><b>Map &amp; Resolve</b> — Each <code className="bg-white px-1 rounded">device_user_id</code> is matched to an <code className="bg-white px-1 rounded">employee_id</code>. Without a mapping the punch is silently skipped (visible in the "Unmapped" tab).</li>
          <li><b>Verify (manual)</b> — Payroll admin opens <b>Biometric Punches</b> page, reviews the day's raw punches and clicks <b>Process Pending</b>. Mappings + cutoff are applied; canonical <code className="bg-white px-1 rounded">punch_logs</code> rows are created.</li>
          <li><b>Calculate</b> — Run <b>Attendance Calculation</b> for the period. Engine combines: punches → present/absent, leaves (approved only), regularization (approved only), holidays, weekly offs → final <code className="bg-white px-1 rounded">attendance_records</code>.</li>
          <li><b>Inbox Cleanup</b> — Resolve any pending Regularization, Leave, and OT requests for the period. Each is approved or rejected from its inbox.</li>
          <li><b>Close &amp; Lock</b> — Click <b>Lock Period</b> on Attendance. After this point, attendance becomes read-only for the period. Payroll Run can now consume it.</li>
        </ol>
      </div>

      <div className="flex flex-wrap gap-1 mt-2">
        <StatusPill ok={a.attendance_records >= 0} label="Records" count={a.attendance_records} />
        <StatusPill ok={a.leave_policies > 0} label="Leave Policies" count={a.leave_policies} />
      </div>

      <Step num="5.1" title="Biometric Device Setup (one-time)" link="/attendance/biometric/devices">
        <ol className="list-decimal ml-4 space-y-1 mt-1">
          <li>Add the device on <b>Attendance → Biometric Devices</b>: serial + display name. The system mints an API key.</li>
          <li>Download the <b>Gateway Agent ZIP</b> (a 1-file Python poller) and run it on a PC inside your office LAN. It polls the device every N minutes and forwards new punches to the cloud over HTTPS.</li>
          <li>Open <b>Attendance → Biometric Mapping</b> and create the <code>device_user_id → employee_id</code> bridge. ESSL / face devices typically use 1, 2, 3… as device user IDs — these aren't your payroll codes.</li>
        </ol>
        <Note>
          <b>Tip:</b> on Biometric Mapping, click <b>Auto-map by Employee Code</b> if your devices were configured with the same employee_code you use in payroll — it'll bulk-create mappings in one click.
        </Note>
      </Step>

      <Step num="5.2" title="🩺 Is my device syncing? — One-click diagnostic" link="/attendance/biometric/devices">
        Open <b>Biometric Devices</b> → click <b>Sync Diagnostic</b>. It walks 6 stages bottom-up and shows the FIRST broken link with a fix:
        <ul className="list-disc ml-4 mt-1 space-y-0.5">
          <li><b>Stage 1 — Devices:</b> any registered &amp; active?</li>
          <li><b>Stage 2 — Settings:</b> cutoff_date OK? auto_process flag?</li>
          <li><b>Stage 3 — Raw punches:</b> arriving in last 30 days?</li>
          <li><b>Stage 4 — Mapping:</b> all device_user_ids mapped?</li>
          <li><b>Stage 5 — Processing:</b> any pending unprocessed punches?</li>
          <li><b>Stage 6 — Attendance:</b> records calculated for recent days?</li>
        </ul>
        <Note>
          <b>Most common cause of "device not syncing":</b> the device sends punches just fine but you haven't mapped device_user_id <code>5</code> to employee <code>HECS-CORP-2026-0049</code>. The diagnostic surfaces this in one click.
        </Note>
      </Step>

      <Step num="5.3" title="Daily verification (manual mode — recommended)" link="/attendance/biometric/punches">
        On <b>Biometric Punches</b> (raw inbox):
        <ol className="list-decimal ml-4 mt-1 space-y-0.5">
          <li>Filter by date = today (or the day under review).</li>
          <li>Skim the list — IN/OUT pairs per employee. Flag obvious anomalies (duplicate punches, single-punch-only, etc.).</li>
          <li>Click <b>Process Pending Punches</b>. Mapped punches move to canonical <code>punch_logs</code>; unmapped ones surface in the "Unmapped" tab.</li>
          <li>Open <b>Attendance Calculation</b> → run for the affected day → attendance_records get refreshed.</li>
        </ol>
        <Note>
          <b>Why manual?</b> Auto-process is fast but lets bad data into payroll silently. Manual verification catches missed punches, sensor errors, and unmapped employees BEFORE they corrupt the register. Switch back to Auto in Biometric Settings once your data is stable.
        </Note>
      </Step>

      <Step num="5.4" title="Other ingestion paths (no biometric / hybrid)" link="/attendance/upload">
        <ul className="list-disc ml-4 space-y-0.5">
          <li><b>CSV bulk upload</b> — one row per employee × day. Used by clients with manual muster registers.</li>
          <li><b>eTimeTrackLite import</b> — for the specific TM-tracker file format.</li>
          <li><b>Single-employee manual entry</b> — for one-off edits.</li>
          <li><b>Web check-in</b> — front-end clock-in / clock-out for office staff (no device).</li>
        </ul>
        Each upload creates an <b>Upload Batch</b> row with full audit trail; the same flow as biometric from step 5.5 onwards.
      </Step>

      <Step num="5.5" title="Leave + Regularization (must clear before lock)" link="/attendance/regularization">
        These are the two ways an employee can ADD to or CORRECT their punch data:
        <ul className="list-disc ml-4 mt-1 space-y-0.5">
          <li><b>Leave Application</b> — Employee applies via ESS for CL / SL / EL / etc. Manager approves. Approved leaves are merged into attendance_records during step 5.6 (Calculate).</li>
          <li><b>Regularization</b> — Employee forgot to punch / device error. Files a regularization request with reason + supporting note. Manager approves → mark-day-as-present is applied during step 5.6.</li>
        </ul>
        <Note>
          <b>Pending requests BLOCK the lock</b>. The Attendance Lock action fails with a list of pending IDs until each is approved or rejected. This is intentional — payroll must not consume unresolved attendance.
        </Note>
      </Step>

      <Step num="5.6" title="Run Attendance Calculation" link="/attendance/calculation">
        Click <b>Bulk Calculate for Period</b>. The engine processes every active employee for every day:
        <ol className="list-decimal ml-4 mt-1 space-y-0.5">
          <li>Start with shift roster (or default cycle) for the day.</li>
          <li>Apply punches → present (first IN + last OUT), late mins, early-out mins, OT mins.</li>
          <li>Apply approved leaves → mark leave_days, reduce payable_days as per leave_type policy.</li>
          <li>Apply approved regularizations → flip absent → present where supported.</li>
          <li>Calculate LOP = base_days − (present + leave_paid + holidays + weekly_offs).</li>
          <li>Compute final <b>payable_days</b> that payroll will use.</li>
        </ol>
        <Note>You can re-run calculation any time before lock. Each run replaces prior records for the period.</Note>
      </Step>

      <Step num="5.7" title="Exceptions resolution" link="/attendance/exceptions">
        High-severity exceptions BLOCK payroll until resolved (or admin overrides them):
        <ul className="list-disc ml-4 mt-1 space-y-0.5">
          <li><b>NO_PUNCH_NO_LEAVE</b>: Employee neither punched nor on leave — likely absence (LOP) or missing data.</li>
          <li><b>ODD_PUNCH_COUNT</b>: Single punch or unmatched IN/OUT pairs.</li>
          <li><b>EXTREME_HOURS</b>: &gt; 16 hours or &lt; 2 hours of detected work — needs review.</li>
          <li><b>UNMAPPED_DEVICE_USER</b>: Punches received but not bridged to an employee.</li>
        </ul>
      </Step>

      <Step num="5.8" title="Lock Attendance (handover to Payroll)" link="/attendance/calculation">
        Once steps 5.5 – 5.7 are clean, click <b>Lock Period</b>. This:
        <ul className="list-disc ml-4 mt-1 space-y-0.5">
          <li>Freezes <code>attendance_records</code> for the period — no further edits without Unlock (admin-only).</li>
          <li>Surfaces "Attendance Locked ✓" on the Payroll Run pre-flight panel.</li>
          <li>Records a lock audit log: who, when, employee count, total payable_days.</li>
        </ul>
        <Note>
          <b>Lock is the formal handover from HR to Payroll.</b> After lock, Payroll Run can be processed; before lock, the run will refuse to start with a "PAYROLL_AWAITS_ATTENDANCE_LOCK" pre-flight blocker.
        </Note>
      </Step>
    </div>
  );
}

// =============== 6. Payroll Run ===============

function SectionRun({ p }) {
  const r = p?.payroll_run || {};
  return (
    <div>
      <p className="text-xs text-slate-600 mt-2">
        Run payroll once attendance is closed and pay-days finalised. The engine handles Staff and O&M flows in a single run.
      </p>
      <div className="flex flex-wrap gap-1 mt-2">
        <StatusPill ok={r.periods > 0} label="Periods" count={r.periods} />
        <StatusPill ok={r.runs_total > 0} label="Runs Created" count={r.runs_total} />
        <StatusPill ok={r.runs_processed > 0} label="Runs Processed" count={r.runs_processed} />
      </div>

      <Step num="6.1" title="Create a Payroll Period" link="/payroll/setup">
        Periods = the calendar month label (e.g. Apr-2026). Each period can have multiple runs (regular / off-cycle / arrear).
      </Step>
      <Step num="6.2" title="Create a Run" link="/payroll/run">
        On the Payroll Run page → <b>+ New Run</b> → pick period → run type (regular / off-cycle / arrear) → Create.
      </Step>
      <Step num="6.3" title="Pre-flight Check (read it carefully)" link="/payroll/run">
        Open the run → the pre-flight panel auto-checks for blockers BEFORE you process:
        <ul className="list-disc ml-4 mt-1 space-y-0.5">
          <li><b>STAFF_MISSING_CTC</b>: Staff employees with no CTC assignment → fix in CTC Planner</li>
          <li><b>OM_MISSING_SKILL</b>: O&M employees with no skill_category → fix in Employees</li>
          <li><b>OM_MISSING_WAGE_RATE</b>: no Wage Master row matches → add in Wage Master</li>
          <li><b>OM_GROUP_MISSING_ACT</b>: CTC group missing wage_basis_act → fix in Business Controls</li>
        </ul>
      </Step>
      <Step num="6.4" title="Process All vs Process by Group" link="/payroll/run">
        <b>Process All</b>: every active employee in the company.
        <b>Process by Group</b>: opens the tree picker → tick parents / subs / leaves → process only that subset.
        Re-run later with different groups → cumulative totals stack on the run header.
      </Step>
      <Step num="6.5" title="Review the Bifurcated Register">
        Click any run row → opens the modal with:
        <ul className="list-disc ml-4 mt-1 space-y-0.5">
          <li><b>KPI strip</b>: gross, deductions, net, employer cost</li>
          <li><b>Group-wise Salary Register</b>: drill-down Parent → Sub → CTC group with subtotals at every level</li>
          <li><b>Bifurcated table</b>: every employee × every salary head as columns</li>
          <li><b>Reports row</b>: 10 single-click downloads</li>
        </ul>
      </Step>
      <Step num="6.6" title="Freeze → Submit for Approval → Approve">
        State machine: <Code>draft → preview → frozen → submitted → approved → completed</Code>.
        Approval requires <b>finance_reviewer</b> role.
      </Step>
    </div>
  );
}

// =============== 7. Statutory ===============

function SectionStatutory({ p }) {
  const s = p?.statutory_masters || {};
  return (
    <div>
      <p className="text-xs text-slate-600 mt-2">
        After payroll is approved, generate statutory filings for the month.
      </p>
      <div className="flex flex-wrap gap-1 mt-2">
        <StatusPill ok={s.pf_configs > 0} label="PF" count={s.pf_configs} />
        <StatusPill ok={s.esi_configs > 0} label="ESI" count={s.esi_configs} />
        <StatusPill ok={s.tds_slabs > 0} label="TDS Slabs" count={s.tds_slabs} />
      </div>

      <Step num="7.1" title="PF / ESI / PT / LWF Working" link="/statutory/working">
        Generates per-employee monthly statutory liability (employee + employer split).
      </Step>
      <Step num="7.2" title="Challan Preparation" link="/statutory/challans">
        Auto-generates challan-ready PDFs / ECR files for upload to EPFO / ESIC portals.
      </Step>
      <Step num="7.3" title="TDS Projection (year-round)" link="/statutory/tds-projection">
        Projects annual tax based on YTD earnings + declarations. Updates monthly TDS deduction automatically.
      </Step>
      <Step num="7.4" title="Annual Tax Worksheet (Form 16 / 24Q)" link="/statutory/tax-worksheet">
        Generates Form 16 Part A + B for every employee, plus 24Q quarterly returns for filing.
      </Step>
    </div>
  );
}

// =============== 8. Reports ===============

function SectionReports() {
  const reports = [
    { name: 'Staff CTC Report', desc: 'Monthly CTC roll-up for white-collar; CSV + PDF.' },
    { name: 'O&M Wage Report', desc: 'Per-day-wage employees with Skill, Act, Location, ₹/day, Basic, Bonus, Net.' },
    { name: 'Skill-Category-wise Salary', desc: 'Headcount + total Basic/Gross/Net by skill category.' },
    { name: 'Attendance vs Payroll', desc: 'Per-employee reconciliation of total / payable / LOP days vs gross.' },
    { name: 'LOP Report', desc: 'All employees with LOP > 0, with computed monetary impact.' },
    { name: 'Department/Site Cost', desc: 'Roll-up of total payroll cost by Department × Location.' },
    { name: 'Payroll Register (Bifurcated)', desc: 'Standard register with every salary head as a column.' },
    { name: 'Group-wise Salary Register', desc: 'Parent → Sub → CTC drill-down with subtotals.' },
    { name: 'Variance Report', desc: 'Diff a run against the previous run; spots changes per employee.' },
    { name: 'Payslips (Bulk)', desc: 'Generate all payslip PDFs in one click.' },
  ];
  return (
    <div>
      <p className="text-xs text-slate-600 mt-2">
        All reports are downloadable from inside the Payroll Run modal. Each is filterable by group and downloadable as CSV (some PDF too).
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3">
        {reports.map((r) => (
          <div key={r.name} className="border border-slate-200 rounded p-2 text-xs">
            <p className="font-semibold text-slate-800">📊 {r.name}</p>
            <p className="text-slate-500 text-[11px] mt-0.5">{r.desc}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// =============== 9. Troubleshoot ===============

function SectionTroubleshoot() {
  const faqs = [
    {
      q: 'My HRA / Bonus / Exgratia are showing ₹0 even though Basic is correct.',
      a: 'The dependent formulas are computed AFTER Basic. Check the salary head: "calc=percentage", "pct_of=BASIC", "pct_val=50" should be set. Save the CTC Group and re-run payroll.',
    },
    {
      q: 'Pre-flight says "OM_MISSING_WAGE_RATE" for some employees.',
      a: 'No row in Wage Master matches their (skill, act, location). Either (a) add a more specific row, OR (b) set a Daily Rate Override on the employee, OR (c) change the group\'s wage_basis_location to a less-specific value (or blank).',
    },
    {
      q: 'I have 109 CTC groups — manually editing each is impossible.',
      a: 'Business Controls → CTC Groups → click "🌳 Export Parent / Sub / Cycle". Edit the CSV in Excel. Re-upload via "📤 Bulk Map Hierarchy + Cycle". One round-trip.',
    },
    {
      q: 'Payroll Run page is blank / white screen.',
      a: 'Browser console will show the React error. Most common: a missing import after a redeploy. Hit Refresh — the latest build was tested clean.',
    },
    {
      q: 'I want to delete all attendance & employee data and start fresh.',
      a: 'Tenant Admin → Data Reset. Dual-approval required (you submit, your fellow admin approves). Statutory & masters are preserved.',
    },
    {
      q: 'How do I run payroll for only one parent group (e.g. just Staff)?',
      a: 'Open the run → Process by Group → tick the "Staff" parent → it auto-selects all its sub-groups + leaves → Run. Subsequent batches pick up the rest.',
    },
    {
      q: 'My pay cycle is 26-25 but the engine processed 1-30 attendance.',
      a: 'Check the CTC Group has pay_cycle_start_day=26, pay_cycle_end_day=25 saved. Re-run the payroll — the engine will recompute the attendance window.',
    },
  ];
  return (
    <div>
      <p className="text-xs text-slate-600 mt-2">
        Common questions when something doesn't add up.
      </p>
      <div className="space-y-2 mt-3">
        {faqs.map((f, i) => (
          <details key={i} className="bg-slate-50 border border-slate-200 rounded p-3 text-xs"
            data-testid={`faq-${i}`}>
            <summary className="font-semibold text-slate-800 cursor-pointer">❓ {f.q}</summary>
            <p className="mt-2 text-slate-600 leading-relaxed">{f.a}</p>
          </details>
        ))}
      </div>
    </div>
  );
}
